import { useQuery } from "@tanstack/react-query";
import { useDataProvider } from "ra-core";

import type { CrmDataProvider } from "../types";
import { getAttachmentPath, SIGNED_URL_TTL_SECONDS } from "./attachments";

/** Reuse a signed URL for most of its lifetime to avoid re-signing on remount. */
const STALE_TIME_MS = (SIGNED_URL_TTL_SECONDS - 10 * 60) * 1000;

/**
 * Resolves a stored attachment value (object path or legacy public URL) to a
 * short-lived signed URL, for display/download only. Never persist the result.
 *
 * Returns:
 * - the value itself, synchronously, when it is not a bucket object (external
 *   or `data:` / `blob:` URL, e.g. a freshly picked file);
 * - `undefined` while the signed URL is loading or when signing failed, so
 *   callers can render a non-broken placeholder;
 * - the signed URL once available.
 */
export const useSignedUrl = (
  value: string | null | undefined,
): string | undefined => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const isBucketObject = !!value && getAttachmentPath(value) != null;

  const { data } = useQuery({
    queryKey: ["signedUrl", value],
    enabled: isBucketObject,
    staleTime: STALE_TIME_MS,
    retry: false,
    // Signed URLs carry a token and expire: keep them out of the persisted cache.
    meta: { persist: false },
    queryFn: async () => {
      const [signed] = await dataProvider.getSignedUrls([value!]);
      // The resolver returns the input unchanged when signing failed.
      if (!signed || signed === value) {
        throw new Error("Failed to sign attachment URL");
      }
      return signed;
    },
  });

  if (!value) return undefined;
  return isBucketObject ? data : value;
};
